"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.1
  });
  const videoWrapperRef = useRef<HTMLDivElement | null>(null);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const parallaxOffset = scrollY * 0.3;
  const contentFade = Math.max(0, 1 - scrollY / 600);
  const contentTranslate = scrollY * 0.4;
  return <section data-section-id="4873" id="spanishherobanner" className="relative h-screen w-full overflow-hidden bg-background text-foreground">
      {}
      <div ref={videoWrapperRef} className="absolute inset-0 w-full h-[120%] will-change-transform" style={{
      transform: `translate3d(0, ${parallaxOffset}px, 0)`
    }}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/medical_innovation_global.webp" alt="Advanced medical technology and diagnostic equipment in a modern clinical setting" className="absolute inset-0 w-full h-full object-cover" loading="eager" decoding="async" />
      </div>

      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "linear-gradient(to top, hsla(178, 22%, 5%, 0.85) 0%, hsla(178, 22%, 5%, 0.55) 40%, hsla(178, 22%, 5%, 0.25) 100%)"
    }} />

      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse at center, transparent 30%, hsla(178, 22%, 5%, 0.55) 100%)"
    }} />

      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      {}
      <div className="absolute top-[6%] left-[6%] md:top-[6%] md:left-[6%] z-10 transition-all duration-1000" style={{
      opacity: isVisible ? contentFade : 0,
      transform: `translateY(${isVisible ? 0 : 20}px)`
    }}>
        <div className="flex items-center gap-2">
          <div className="h-px w-8 bg-primary" />
          <span className="font-default text-[10px] md:text-xs tracking-[0.2em] uppercase" style={{
          color: "hsl(178 65% 72%)"
        }}>
            Global Medical Sourcing & Distribution Partner
          </span>
        </div>
      </div>

      {}
      <div className="absolute top-[6%] right-[6%] z-10 hidden md:flex flex-col items-end gap-3 transition-all duration-1000" style={{
      opacity: isVisible ? contentFade : 0,
      transform: `translateY(${isVisible ? 0 : 20}px)`
    }}>
        <div className="flex items-center gap-2">
          <span className="relative inline-flex h-2 w-2 rounded-lg" style={{
          backgroundColor: "hsl(178 65% 72%)"
        }}>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-lg opacity-75" style={{
            backgroundColor: "hsl(178 65% 72%)"
          }} />
          </span>
          <span className="font-default text-[10px] tracking-[0.2em] uppercase text-white/60">
            Global Reach
          </span>
        </div>
        <div className="flex flex-col items-end gap-1 font-default text-[10px] md:text-xs tracking-[0.18em] text-white/70">
          <span>CLINICAL EVALUATION</span>
          <span>REGULATORY SUPPORT</span>
          <span>STRATEGIC PARTNERSHIPS</span>
        </div>
      </div>

      {}
      <div ref={contentRef} className="absolute inset-0 z-10 flex items-end" style={{
      opacity: contentFade,
      transform: `translateY(-${contentTranslate}px)`
    }}>
        <div className="w-full px-[6%] md:px-[8%] pb-[10%] md:pb-[8%]">
          <div className="max-w-3xl transition-all duration-1000 ease-out" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(40px)"
        }}>
            {}
            <div className="mb-6 flex items-center gap-3">
              <div className="h-px w-10 bg-primary" />
              <span className="font-default text-[10px] md:text-xs tracking-[0.25em] uppercase text-white/70">
                MS Biotechnology &middot; Trusted Innovation
              </span>
            </div>

            {}
            <h1 className="font-default font-light text-white" style={{
            fontSize: "clamp(64px, 11vw, 180px)",
            lineHeight: 0.9,
            letterSpacing: "-0.04em"
          }}>
              Empowering <em className="italic font-light">Healthcare.</em>
            </h1>

            {}
            <p className="mt-6 md:mt-8 font-default text-base md:text-lg text-white/80 leading-relaxed" style={{
            maxWidth: "480px"
          }}>
              Connecting innovative medical manufacturers with healthcare professionals worldwide through expert sourcing and strategic distribution.
            </p>

            {}
            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
              <Button asChild size="lg" className="h-12 px-7 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-medium tracking-wide group">
                <Link to="/OurProductsPage">
                  Explore Our Solutions
                  <ArrowRight className="ml-1 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Button>

              <Link to="/CompliancePage" className="group relative inline-flex items-center self-start text-white font-default text-sm tracking-[0.15em] uppercase">
                <span className="relative">
                  Our Compliance
                  <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-100 bg-white transition-transform duration-500 group-hover:scale-x-0" />
                  <span className="absolute -bottom-1 left-0 h-px w-full origin-right scale-x-0 bg-primary transition-transform duration-500 group-hover:origin-left group-hover:scale-x-100" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-[6%] right-[6%] z-10 hidden md:flex flex-col items-center gap-3" style={{
      opacity: contentFade
    }}>
        <span className="font-default text-[10px] tracking-[0.3em] uppercase text-white/60 [writing-mode:vertical-rl] rotate-180">
          SCROLL
        </span>
        <div className="relative h-16 w-px overflow-hidden bg-white/15">
          <span className="absolute left-0 top-0 h-6 w-px bg-white/80" style={{
          animation: "forge-scroll-down 2.2s cubic-bezier(0.65, 0, 0.35, 1) infinite"
        }} />
        </div>
      </div>

      {}
      <div className="absolute bottom-[4%] left-1/2 -translate-x-1/2 z-10 flex md:hidden flex-col items-center gap-2" style={{
      opacity: contentFade
    }}>
        <div className="relative h-10 w-px overflow-hidden bg-white/15">
          <span className="absolute left-0 top-0 h-4 w-px bg-white/80" style={{
          animation: "forge-scroll-down 2.2s cubic-bezier(0.65, 0, 0.35, 1) infinite"
        }} />
        </div>
        <span className="font-default text-[9px] tracking-[0.3em] uppercase text-white/60">
          SCROLL
        </span>
      </div>

      <style>{`
        @keyframes forge-scroll-down {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(400%); }
        }
      `}</style>
    </section>;
}